import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CalendarClock, CheckCircle2, CircleAlert, FlaskConical, Loader2, MessageCircle, RefreshCw, Send, UserRound } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import Modal from '@/components/ui/Modal';
import {
  IDIOMAS, NOMBRE_IDIOMA, canalDe, versionEmpezada,
  type AjustesComunicaciones, type Campana, type CampanaEditable, type Idioma, type Revision,
} from '@/lib/comunicaciones';

/**
 * Paso 3: remitente, revisión, prueba y envío. La revisión la hace el
 * servidor con la misma regla que el envío, después de guardar lo último que
 * se haya escrito.
 */
export default function PasoEnviar({ campana, ajustes, onChange, guardarYa, onLanzada }: {
  campana: Campana;
  ajustes: AjustesComunicaciones | null;
  onChange: (cambios: Partial<CampanaEditable>) => void;
  /** Guarda lo pendiente; resuelve cuando el servidor tiene la última versión. */
  guardarYa: () => Promise<boolean>;
  onLanzada: () => void;
}) {
  const [revision, setRevision] = useState<Revision | null>(null);
  const [revisando, setRevisando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const revisar = useCallback(async (): Promise<Revision | null> => {
    setRevisando(true);
    setError(null);
    try {
      if (!(await guardarYa())) throw new Error('No se han podido guardar los últimos cambios');
      const r = await api.getRevisionCampana(campana.id);
      setRevision(r);
      return r;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo revisar');
      return null;
    } finally {
      setRevisando(false);
    }
  }, [campana.id, guardarYa]);

  // Revisar al entrar y cuando cambie el remitente
  const claveRemitente = `${campana.remitente.direccion}|${campana.remitente.responderA}`;
  useEffect(() => {
    const t = setTimeout(() => { void revisar(); }, 400);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claveRemitente]);

  const lista = revision && revision.errores.length === 0 && revision.recuento.total > 0;
  const whatsapp = canalDe(campana) === 'whatsapp';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
      <div className="space-y-4">
        {whatsapp ? <><NumeroWhatsApp /><PruebaWhatsApp campana={campana} guardarYa={guardarYa} /></> : <>
        <div className="card space-y-3">
          <div className="flex items-center gap-2 font-semibold text-sereno-dark"><UserRound size={16} /> Remitente</div>
          <div>
            <label className="label">Desde</label>
            <select
              className="input"
              value={campana.remitente.direccion}
              onChange={e => onChange({ remitente: { ...campana.remitente, direccion: e.target.value } })}
            >
              {(ajustes?.remitentes || [{ direccion: campana.remitente.direccion, nombre: '' }]).map(r => (
                <option key={r.direccion} value={r.direccion}>{r.nombre ? `${r.nombre} <${r.direccion}>` : r.direccion}</option>
              ))}
            </select>
            <p className="text-[11px] text-sereno-dark/35 mt-1">Los remitentes se dan de alta en Comunicaciones → Ajustes.</p>
          </div>
          <div>
            <label className="label">Las respuestas van a</label>
            <input
              className="input"
              type="email"
              value={campana.remitente.responderA}
              placeholder={ajustes?.responderAPorDefecto || 'gestion@tusereno.es'}
              onChange={e => onChange({ remitente: { ...campana.remitente, responderA: e.target.value } })}
            />
          </div>
        </div>

        <Prueba campana={campana} guardarYa={guardarYa} />
        </>}
      </div>

      <div className="space-y-4">
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <div className="font-semibold text-sereno-dark">Revisión</div>
            <button type="button" onClick={() => void revisar()} disabled={revisando} className="btn-ghost text-xs flex items-center gap-1.5 px-2 py-1">
              {revisando ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Revisar
            </button>
          </div>
          {error && <div className="text-sm text-red-600 flex items-center gap-1.5"><CircleAlert size={15} /> {error}</div>}
          {revision && (
            <div className="space-y-3">
              {revision.errores.length > 0 ? (
                <ul className="space-y-1">
                  {revision.errores.map((e, i) => (
                    <li key={i} className="text-sm text-red-600 flex items-start gap-1.5"><CircleAlert size={15} className="mt-0.5 shrink-0" /> {e}</li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-emerald-700 flex items-center gap-1.5"><CheckCircle2 size={15} /> Lista para enviar{revision.avisos.length > 0 && ', con avisos'}</div>
              )}
              {revision.avisos.length > 0 && (
                <ul className="space-y-1">
                  {revision.avisos.map((a, i) => (
                    <li key={i} className="text-sm text-amber-700 flex items-start gap-1.5"><AlertTriangle size={15} className="mt-0.5 shrink-0" /> {a}</li>
                  ))}
                </ul>
              )}
              <div className="text-sm text-sereno-dark/70">
                <strong className="text-sereno-dark text-lg tabular-nums">{revision.recuento.total}</strong> destinatarios
              </div>
              <ul className="text-sm text-sereno-dark/60 space-y-0.5">
                {Object.entries(revision.porVersion).filter(([, n]) => n).map(([i, n]) => (
                  <li key={i}>
                    {i === 'ninguna'
                      ? <span className="text-red-600">{n} no tienen versión de la plantilla que recibir</span>
                      : `${n} recibirán la versión en ${(NOMBRE_IDIOMA[i as Idioma] || i).toLowerCase()}`}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <Lanzar campana={campana} lista={!!lista} total={revision?.recuento.total || 0} revisar={revisar} guardarYa={guardarYa} onLanzada={onLanzada} />
      </div>
    </div>
  );
}

/** Desde qué número sale: el de WhatsApp de Sereno, el mismo que usa Hostify. */
function NumeroWhatsApp() {
  const { data, error } = useApi(() => api.getEstadoWhatsApp(), []);
  return (
    <div className="card space-y-2">
      <div className="flex items-center gap-2 font-semibold text-sereno-dark"><MessageCircle size={16} /> Sale desde</div>
      {error && <div className="text-sm text-red-600">{error}</div>}
      {data?.numero ? (
        <div className="text-sm text-sereno-dark/70">
          <strong className="text-sereno-dark">{data.numero.verified_name}</strong> · {data.numero.display_phone_number}
          {data.numero.quality_rating && <span className="text-sereno-dark/40"> · calidad {data.numero.quality_rating.toLowerCase()}</span>}
        </div>
      ) : data && <div className="text-sm text-amber-700">{data.errorNumero || 'WhatsApp no está conectado todavía: Comunicaciones → Ajustes.'}</div>}
      <p className="text-[11px] text-sereno-dark/35">Es el mismo número con el que Hostify escribe a los huéspedes: las respuestas llegan también a su bandeja.</p>
    </div>
  );
}

function PruebaWhatsApp({ campana, guardarYa }: { campana: Campana; guardarYa: () => Promise<boolean> }) {
  const [telefonos, setTelefonos] = useState('');
  const [idioma, setIdioma] = useState<Idioma>(campana.idiomaReserva);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const enviar = async () => {
    const lista = telefonos.split(/[,;\n]+/).map(x => x.trim()).filter(Boolean);
    if (lista.length === 0) return setError('Escribe al menos un teléfono con prefijo (+34…)');
    setEnviando(true);
    setError(null);
    setResultado(null);
    setAviso(null);
    try {
      await guardarYa();
      const r = await api.enviarPruebaWhatsApp(campana.id, lista, idioma);
      setAviso(r.aviso || null);
      const mal = r.resultados.filter(x => x.estado !== 'enviado');
      if (mal.length) setError(mal.map(x => `${x.para}: ${x.detalle || x.estado}`).join(' · '));
      else setResultado(`Enviada a ${r.resultados.map(x => x.para).join(', ')} (versión ${r.idioma}).`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo enviar');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="card space-y-3">
      <div className="flex items-center gap-2 font-semibold text-sereno-dark"><FlaskConical size={16} /> Enviar una prueba</div>
      <p className="text-sm text-sereno-dark/50">Llega la plantilla de verdad, con «Laura» como nombre. Meta cobra el mensaje como cualquier otro. Si contestas en los 7 días siguientes, la respuesta entra en Conversaciones como la de un cliente.</p>
      <div className="flex gap-2">
        <input className="input flex-1" placeholder="+34 600 000 000" value={telefonos} onChange={e => setTelefonos(e.target.value)} />
        <select className="input w-auto" value={idioma} onChange={e => setIdioma(e.target.value as Idioma)}>
          {IDIOMAS.map(i => <option key={i} value={i}>{i.toUpperCase()}</option>)}
        </select>
      </div>
      <button type="button" onClick={enviar} disabled={enviando} className="btn-ghost border border-gray-200 text-sm flex items-center gap-1.5">
        {enviando ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Enviar prueba
      </button>
      {resultado && <div className="text-sm text-emerald-700">{resultado}</div>}
      {aviso && <div className="text-sm text-amber-700">{aviso}</div>}
      {error && <div className="text-sm text-red-600">{error}</div>}
    </div>
  );
}

function Prueba({ campana, guardarYa }: { campana: Campana; guardarYa: () => Promise<boolean> }) {
  const empezadas = IDIOMAS.filter(i => versionEmpezada(campana.versiones[i]));
  const [para, setPara] = useState('');
  const [idioma, setIdioma] = useState<Idioma>(empezadas.includes(campana.idiomaReserva) ? campana.idiomaReserva : empezadas[0] || 'es');
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const enviar = async () => {
    const direcciones = para.split(/[\s,;]+/).map(x => x.trim()).filter(Boolean);
    if (direcciones.length === 0) return setError('Escribe al menos una dirección');
    setEnviando(true);
    setError(null);
    setResultado(null);
    try {
      await guardarYa();
      const r = await api.enviarPruebaCampana(campana.id, direcciones, idioma);
      const mal = r.resultados.filter(x => x.estado !== 'enviado');
      if (mal.length) setError(mal.map(x => `${x.para}: ${x.detalle || x.estado}`).join(' · '));
      else setResultado(`Enviada a ${r.resultados.map(x => x.para).join(', ')}. Mírala en Gmail, Outlook y el móvil.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo enviar');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="card space-y-3">
      <div className="flex items-center gap-2 font-semibold text-sereno-dark"><FlaskConical size={16} /> Enviar una prueba</div>
      <p className="text-sm text-sereno-dark/50">Llega con «[Prueba]» en el asunto y «Laura» como nombre. Sin seguimiento: no cuenta en el informe.</p>
      <div className="flex gap-2">
        <input className="input flex-1" placeholder="tu@correo.com, otra@correo.com" value={para} onChange={e => setPara(e.target.value)} />
        <select className="input w-auto" value={idioma} onChange={e => setIdioma(e.target.value as Idioma)}>
          {(empezadas.length ? empezadas : IDIOMAS).map(i => <option key={i} value={i}>{i.toUpperCase()}</option>)}
        </select>
      </div>
      <button type="button" onClick={enviar} disabled={enviando} className="btn-ghost border border-gray-200 text-sm flex items-center gap-1.5">
        {enviando ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Enviar prueba
      </button>
      {resultado && <div className="text-sm text-emerald-700">{resultado}</div>}
      {error && <div className="text-sm text-red-600">{error}</div>}
    </div>
  );
}

/** «2026-10-06T09:30» en hora local, para el valor por defecto del selector. */
function mananaALas(hora: number): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(hora, 0, 0, 0);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function Lanzar({ campana, lista, total, revisar, guardarYa, onLanzada }: {
  campana: Campana; lista: boolean; total: number;
  revisar: () => Promise<Revision | null>;
  guardarYa: () => Promise<boolean>; onLanzada: () => void;
}) {
  const [confirmar, setConfirmar] = useState<'enviar' | 'programar' | null>(null);
  /** Los avisos que se enseñan al confirmar: se mandan al servidor, que comprueba que no haya otros. */
  const [avisos, setAvisos] = useState<string[]>([]);
  const [cuando, setCuando] = useState(mananaALas(10));
  const [trabajando, setTrabajando] = useState<'enviar' | 'programar' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const accion = async (tipo: 'enviar' | 'programar', aceptados: string[]) => {
    setTrabajando(tipo);
    setError(null);
    try {
      if (!(await guardarYa())) throw new Error('No se han podido guardar los últimos cambios');
      if (tipo === 'enviar') await api.enviarCampana(campana.id, aceptados);
      else await api.programarCampana(campana.id, new Date(cuando).toISOString(), aceptados);
      setConfirmar(null);
      onLanzada();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo');
    } finally {
      setTrabajando(null);
    }
  };

  // Antes de confirmar se revisa otra vez: lo que se confirma es lo de ahora,
  // no lo de cuando se abrió la pantalla (Meta puede haber rechazado un idioma).
  const pedir = async (tipo: 'enviar' | 'programar') => {
    setTrabajando(tipo);
    setError(null);
    const r = await revisar();
    setTrabajando(null);
    if (!r || r.errores.length > 0 || r.recuento.total === 0) return; // la revisión ya dice por qué
    setAvisos(r.avisos);
    if (tipo === 'programar' && r.avisos.length === 0) return accion('programar', []);
    setConfirmar(tipo);
  };

  const fecha = new Date(cuando);
  return (
    <div className="card space-y-4">
      <div className="font-semibold text-sereno-dark">Enviar</div>
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="label">Programar para</label>
          <input type="datetime-local" className="input" value={cuando} onChange={e => setCuando(e.target.value)} />
        </div>
        <button type="button" disabled={!lista || !!trabajando} onClick={() => void pedir('programar')} className="btn-ghost border border-gray-200 text-sm flex items-center gap-1.5 disabled:opacity-40">
          {trabajando === 'programar' ? <Loader2 size={14} className="animate-spin" /> : <CalendarClock size={14} />} Programar
        </button>
      </div>
      <div className="border-t border-gray-100 pt-4">
        <button type="button" disabled={!lista || !!trabajando} onClick={() => void pedir('enviar')} className="btn-primary w-full flex items-center justify-center gap-2">
          {trabajando === 'enviar' && !confirmar ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Enviar ahora a {total} destinatarios
        </button>
        <p className="text-[11px] text-sereno-dark/35 mt-2">
          {canalDe(campana) === 'whatsapp'
            ? 'Sale en unos minutos. Se puede pausar o cancelar mientras sale.'
            : 'Sale poco a poco, al ritmo de los ajustes, para no comerse la cuota de Azure. Se puede pausar o cancelar mientras sale.'}
        </p>
      </div>
      {error && !confirmar && <div className="text-sm text-red-600">{error}</div>}

      <Modal isOpen={!!confirmar} onClose={() => setConfirmar(null)} title={confirmar === 'programar' ? '¿Programar la campaña?' : '¿Enviar la campaña?'} size="sm">
        {avisos.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 mb-4 space-y-1.5">
            <div className="text-sm font-semibold text-amber-800 flex items-center gap-1.5"><AlertTriangle size={15} /> Antes de lanzarla</div>
            <ul className="space-y-1 list-disc pl-5">
              {avisos.map((a, i) => <li key={i} className="text-sm text-amber-800">{a}</li>)}
            </ul>
          </div>
        )}
        <p className="text-sm text-sereno-dark/70 mb-1">
          «{campana.nombre}» a <strong>{total}</strong> destinatarios
          {confirmar === 'programar' && !Number.isNaN(fecha.getTime()) && <>, el {fecha.toLocaleString('es-ES', { dateStyle: 'long', timeStyle: 'short' })}</>}.
        </p>
        <p className="text-sm text-sereno-dark/50 mb-5">
          {confirmar === 'programar'
            ? 'Hasta esa hora se puede desprogramar y volver a editar.'
            : 'Desde aquí ya no se puede editar el contenido. Si algo no cuadra, se pausa o se cancela, pero lo que ya salió no vuelve.'}
        </p>
        {error && <div className="text-sm text-red-600 mb-3">{error}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost text-sm" onClick={() => setConfirmar(null)}>Todavía no</button>
          <button type="button" className="btn-primary text-sm flex items-center gap-1.5" disabled={!!trabajando} onClick={() => void accion(confirmar!, avisos)}>
            {trabajando ? <Loader2 size={14} className="animate-spin" /> : confirmar === 'programar' ? <CalendarClock size={14} /> : <Send size={14} />}
            {confirmar === 'programar' ? 'Programar' : 'Enviar'}{avisos.length > 0 && ' igualmente'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
